import { Capacitor } from '@capacitor/core';
import type { ActionPerformed } from '@capacitor/push-notifications';
import { canAccessApp, clientDebugLog } from '@apexx/auth';
import { resolvePushHubDestination, type PushHubDestination } from '@apexx/shell-contract';

import { shellOrchestrator } from '@/shell/orchestrator';

import {
	clearPendingPush,
	hasPendingPush,
	savePendingPush,
	takePendingPush,
} from './pendingPushStore';

type PushNavRouter = {
	push: (to: string | { name: string; query?: Record<string, string> }) => Promise<unknown> | unknown;
	replace: (to: string | { name: string; query?: Record<string, string> }) => Promise<unknown> | unknown;
};

type PushNavAuth = {
	isLoggedIn: boolean;
	accessToken?: string;
};

type PushActionHandler = (action: ActionPerformed) => Promise<void>;

let listenerRegistered = false;
let queuedAction: ActionPerformed | null = null;
let activeHandler: PushActionHandler | null = null;

function pushLog(
	level: 'info' | 'warn',
	message: string,
	details?: Record<string, unknown>,
): void {
	try {
		clientDebugLog(level, `[hub:push] ${message}`, details);
	} catch {
		try {
			if (level === 'warn') {
				console.warn(`[hub:push] ${message}`, details);
			} else {
				console.info(`[hub:push] ${message}`, details);
			}
		} catch {
			// ignore
		}
	}
}

function extractPushData(action: ActionPerformed): Record<string, unknown> {
	const data = action.notification?.data;
	if (data && typeof data === 'object') {
		return data as Record<string, unknown>;
	}

	return {};
}

export async function navigateToPushDestination(
	router: PushNavRouter,
	destination: PushHubDestination,
): Promise<void> {
	pushLog('info', 'navigating to push destination', {
		hubPath: destination.hubPath,
		appId: destination.appId,
		subPath: destination.subPath,
	});
	await router.replace(destination.hubPath);
	// Hub replace alone is not enough when the sub-app is already mounted.
	await shellOrchestrator.syncActiveSubPath(destination.appId, destination.subPath);
}

async function navigateToDestinationOrLogin(
	router: PushNavRouter,
	auth: PushNavAuth,
	destination: PushHubDestination,
	type: string,
): Promise<void> {
	if (!auth.isLoggedIn) {
		pushLog('info', 'persisting pending push; navigating to login', {
			type,
			hubPath: destination.hubPath,
		});
		await savePendingPush(destination, type);
		await router.replace({ name: 'login' });
		return;
	}

	if (!canAccessApp(auth.accessToken, destination.requiredRole)) {
		pushLog('warn', 'user lacks required role for push destination', {
			requiredRole: destination.requiredRole,
		});
		await clearPendingPush();
		await router.replace({ name: 'launcher' });
		return;
	}

	await clearPendingPush();
	await navigateToPushDestination(router, destination);
}

async function handlePushAction(
	action: ActionPerformed,
	router: PushNavRouter,
	getAuth: () => PushNavAuth,
): Promise<void> {
	const data = extractPushData(action);
	const destination = resolvePushHubDestination(data);

	pushLog('info', 'action performed', {
		actionId: action.actionId,
		type: typeof data.type === 'string' ? data.type : undefined,
		hasDestination: destination !== null,
	});

	if (!destination) {
		pushLog('info', 'ignoring push action with no resolvable destination', {
			keys: Object.keys(data),
		});
		return;
	}

	const type = typeof data.type === 'string' ? data.type : destination.appId;
	await navigateToDestinationOrLogin(router, getAuth(), destination, type);
}

function deliverPushAction(action: ActionPerformed): void {
	if (activeHandler) {
		void activeHandler(action);
		return;
	}

	pushLog('info', 'queuing push action until hub bootstrap finishes');
	queuedAction = action;

	// Persist while queued so cold-start resume still works if the in-memory
	// event is lost between early listener attach and post-mount bind.
	const data = extractPushData(action);
	const destination = resolvePushHubDestination(data);
	if (destination) {
		const type = typeof data.type === 'string' ? data.type : destination.appId;
		void savePendingPush(destination, type).catch((error: unknown) => {
			pushLog('warn', 'failed to persist queued push destination', {
				error: error instanceof Error ? error.message : String(error),
			});
		});
	}
}

/**
 * Register Capacitor push tap listener early so cold-start launches are not lost.
 * Capacitor re-emits the launch notification when this listener attaches.
 */
export async function setupPushNavigationListenerEarly(): Promise<void> {
	if (!Capacitor.isNativePlatform() || listenerRegistered) {
		return;
	}

	listenerRegistered = true;
	pushLog('info', 'registering pushNotificationActionPerformed', {
		platform: Capacitor.getPlatform(),
	});

	const { PushNotifications } = await import('@capacitor/push-notifications');

	await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
		try {
			deliverPushAction(action);
		} finally {
			pushLog('info', 'pushNotificationActionPerformed received', {
				actionId: action.actionId,
				notificationId: action.notification?.id,
			});
		}
	});
}

/**
 * Bind router/auth handling after bootstrap. Drains any queued cold-start tap.
 */
export async function bindPushNavigationHandler(options: {
	router: PushNavRouter;
	getAuth: () => PushNavAuth;
}): Promise<void> {
	if (!Capacitor.isNativePlatform()) {
		return;
	}

	activeHandler = async (action) => {
		try {
			await handlePushAction(action, options.router, options.getAuth);
		} catch (error) {
			pushLog('warn', 'Failed to handle push notification tap', {
				error: error instanceof Error ? error.message : String(error),
			});
		}
	};

	pushLog('info', 'push handler bound', {
		hasQueuedAction: queuedAction !== null,
	});

	if (queuedAction) {
		const action = queuedAction;
		queuedAction = null;
		await activeHandler(action);
	}
}

/**
 * Destination path for a pending push after auth (consumes Preferences so login
 * redirects do not loop). Prefer calling {@link resumePendingPushIfAny} when
 * embedded sub-path sync is needed.
 */
export async function resolvePostAuthPushDestination(
	accessToken: string | undefined,
): Promise<string | undefined> {
	const pending = await takePendingPush();
	if (!pending) {
		return undefined;
	}

	if (!canAccessApp(accessToken, pending.requiredRole)) {
		pushLog('warn', 'pending push discarded; missing role', {
			requiredRole: pending.requiredRole,
		});
		return undefined;
	}

	return pending.hubPath;
}

/** True when a non-expired pending push destination is stored. */
export async function hasPendingPushDestination(): Promise<boolean> {
	return hasPendingPush();
}

/** Resume a pending push after successful login (prefers push over share). */
export async function resumePendingPushIfAny(
	router: PushNavRouter,
	accessToken: string | undefined,
): Promise<boolean> {
	const pending = await takePendingPush();
	if (!pending) {
		pushLog('info', 'no pending push to resume after auth');
		return false;
	}

	if (!canAccessApp(accessToken, pending.requiredRole)) {
		pushLog('warn', 'pending push resume blocked by role; sending to launcher', {
			requiredRole: pending.requiredRole,
		});
		await router.replace({ name: 'launcher' });
		return true;
	}

	pushLog('info', 'resuming pending push after auth', {
		hubPath: pending.hubPath,
		type: pending.type,
	});
	await navigateToPushDestination(router, pending);
	return true;
}
